import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  Maybe,
  SharedContact,
} from '@xing-com/crate-common-graphql-types';
import { Facepile, FacepileActorProps } from '@xing-com/facepile';
import type { FlagVariant } from '@xing-com/flag';
import { Marker } from '@xing-com/marker';
import { ProfileImage } from '@xing-com/profile-image';
import { BodyCopy, Headline } from '@xing-com/typography';

import { getReasonCombinationOrSingleText } from './helpers';
import { Styled } from './styles';

export type MemberCardDataType = {
  aboutMe?: Array<string>;
  displayFlag?: FlagVariant;
  displayName: string;
  occupationHeadline?: string;
  occupationSubline?: string;
  pageName?: string;
  profileId: string;
  profileImageUrl?: string;
  reason?: Array<string>;
  sharedContacts?: Maybe<Array<SharedContact>>;
  totalSharedContactsWeb?: number;
  trackingToken?: string;
};

export type MemberCardProps = {
  data: MemberCardDataType;
};

export const MemberCard: React.FC<MemberCardProps> = memo(({ data }) => {
  const {
    aboutMe,
    displayFlag,
    displayName,
    occupationHeadline,
    occupationSubline,
    pageName,
    profileId,
    profileImageUrl,
    reason,
    sharedContacts,
    totalSharedContactsWeb,
  } = data;

  const reasonCombinationOrSingleText = getReasonCombinationOrSingleText(
    reason || [],
    totalSharedContactsWeb || 0
  );

  const sharedContactsImageUrlList =
    sharedContacts
      ?.slice(0, 3)
      .map(
        (contact) =>
          (contact.xingId?.profileImage &&
            contact.xingId?.profileImage[0]?.url) ||
          ''
      ) || [];

  const hasSharedContacts = (sharedContacts?.length ?? 0) > 0;

  const profileToFacepileActorProps = (
    profileImageUrlList: string[],
    isBlurred?: boolean
  ): FacepileActorProps[] => {
    return profileImageUrlList.map((url, index) => {
      return {
        id: index.toString() || '',
        src: url,
        loading: 'lazy',
        blur: isBlurred,
      };
    });
  };

  return (
    <Styled.MemberCard data-qa={`member-card-${profileId}`}>
      <Styled.MemberInfo to={`/profile/${pageName}`}>
        <ProfileImage size="large" src={profileImageUrl} alt={displayName} />
        <div>
          {displayFlag && (
            <Styled.MemberFlag size="small" variant={displayFlag} />
          )}
          <Styled.MemberName size="small">{displayName}</Styled.MemberName>
          <Styled.MemberOccupation size="small" noMargin>
            {(occupationHeadline ?? '') +
              (occupationHeadline && occupationSubline ? ' · ' : '') +
              (occupationSubline ?? '')}
          </Styled.MemberOccupation>
        </div>
      </Styled.MemberInfo>
      {reasonCombinationOrSingleText ? (
        <BodyCopy size="small">
          {'🤝' + ' '}
          <FormattedMessage
            id={reasonCombinationOrSingleText}
            values={{ count: totalSharedContactsWeb ?? 0 }}
          />
        </BodyCopy>
      ) : null}
      <Headline size="large">
        <FormattedMessage id={'NETWORK_MYMK_ABOUT_ME_TITLE'} />
      </Headline>
      <Styled.MemberAboutMe $hasSharedContacts={hasSharedContacts}>
        {aboutMe?.map((item) => (
          <Marker key={item}>{item}</Marker>
        ))}
      </Styled.MemberAboutMe>
      {sharedContacts?.length ? (
        <>
          <Headline size="large">
            <FormattedMessage id={'NETWORK_MYMK_TOTAL_SHARED_CONTACTS_WEB'} />
          </Headline>
          <Styled.MemberSharedContacts>
            <Facepile
              size="medium"
              sizeConfined="medium"
              actors={
                profileToFacepileActorProps(
                  sharedContactsImageUrlList,
                  false
                ) || []
              }
              maxActors={3}
            />
            <BodyCopy fontWeight="bold" noMargin>
              {totalSharedContactsWeb && totalSharedContactsWeb > 3
                ? `+${totalSharedContactsWeb - 3}`
                : ''}
            </BodyCopy>
          </Styled.MemberSharedContacts>
        </>
      ) : null}
    </Styled.MemberCard>
  );
});
